/*
 * Classic (non-module) script.
 *
 * Loaded synchronously in <head> before the GTM tag so that the
 * `consent-default` push happens before `gtm.js` starts.
 *
 * Exposes the class on `window.IubendaConsent` and is consumed by
 * `dataLayer.js` (ES module) and `iubenda-plugin.liquid` through the
 * same global
 */
(function () {
  const COOKIE_CONSENT = 'bsnm_consent_native';
  const HOURS_COOKIE_DURATION = 24;
  const DAYS_OF_DURATION = 30;

  // Set by the X, so the preferences saved right after aren't tracked as accept/reject too
  let closedWithX = false;

  /**
   * @class IubendaConsent
   * @description Manages consent cookie, Google Consent Mode, and Iubenda/Shopify Privacy bridge.
   */
  class IubendaConsent {
    /**
     * @description Reads and parses the bsnm_consent_native cookie.
     * @returns {ConsentState}
     */
    static getConsentCookie() {
      const cookies = document.cookie.split('; ');
      const cookie = cookies.find((row) => row.startsWith(COOKIE_CONSENT + '='));
      if (cookie) {
        try {
          const parsedConsent = JSON.parse(decodeURIComponent(cookie.split('=')[1]));
          return {
            analytics: parsedConsent.analytics ?? this.checkConsentMode(),
            marketing: parsedConsent.marketing ?? false,
            preferences: parsedConsent.preferences ?? false,
          };
        } catch (e) {
          return { analytics: this.checkConsentMode(), marketing: false, preferences: false };
        }
      }
      return { analytics: this.checkConsentMode(), marketing: false, preferences: false };
    }

    /**
     * @description Sets the bsnm_consent_native cookie with the given consent values.
     * @param {ConsentState} consent
     */
    static setConsentCookie(consent) {
      const encodedValue = encodeURIComponent(JSON.stringify(consent));
      const expires = new Date(Date.now() + HOURS_COOKIE_DURATION * DAYS_OF_DURATION * 60 * 60 * 1000).toUTCString();
      document.cookie = `${COOKIE_CONSENT}=${encodedValue}; expires=${expires}; path=/`;
    }

    /**
     * @description Checks if the current page load is under Consent Mode based on the presence of a global variable set by the server.
     * @returns {boolean}
     */
    static checkConsentMode() {
      return window.consentModeDefault === 'true';
    }

    /**
     * @description Builds a Google Consent Mode object from the current consent state.
     * @param {ConsentState} consent
     * @returns {Record<string, string>}
     */
    static buildConsentModeObject(consent) {
      return {
        ad_storage: consent.marketing ? 'granted' : 'denied',
        analytics_storage: this.checkConsentMode() ? 'granted' : consent.analytics ? 'granted' : 'denied',
        ad_user_data: consent.marketing ? 'granted' : 'denied',
        ad_personalization: consent.marketing ? 'granted' : 'denied',
        functionality_storage: consent.preferences ? 'granted' : 'denied',
        personalization_storage: 'denied',
        security_storage: consent.preferences ? 'granted' : 'denied',
      };
    }

    /**
     * @description Conditional debug log, prints to console only when a
     * cookie matching `name` is present. Inlined here so this file has no
     * ES module imports and can be loaded as a classic script.
     * @param {string} name
     * @param {...any} args
     */
    static debug(name, ...args) {
      if (document.cookie.split(';').some((c) => c.trim().startsWith(name + '='))) {
        console.log(name, ...args);
      }
    }

    /**
     * @description Initializes Google Consent Mode with defaults from the bsnm_consent_native cookie.
     * Pushes a 'consent-default' event to the dataLayer.
     *
     * Idempotent: subsequent calls are no-ops. The intended call site is the
     * synchronous `<script>` in the document <head> rendered before the GTM
     * tag (see `gtm-consent-default.liquid`); `dataLayer.js` calls this too
     * as a safety net when GTM is not configured.
     */
    static initDefaultConsent() {
      function gtag() {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(arguments);
      }
      window.dataLayer = window.dataLayer || [];
      const consent = this.getConsentCookie();
      const consentObject = this.buildConsentModeObject(consent);

      gtag('consent', 'default', consentObject);
      window.dataLayer.push({ event: 'consent-default' });
      IubendaConsent.debug('dataLayerDebug', 'consent-default', consentObject);
    }

    /**
     * @description Updates Google Consent Mode after user interaction (e.g. Iubenda preference expressed).
     * Reads the current cookie, pushes a consent update and fires a 'consent-update' event.
     * @param {Partial<ConsentState>} [newConsent]
     */
    static updateConsent(newConsent) {
      // use this function to override default gtag function
      function gtag() {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(arguments);
      }
      window.dataLayer = window.dataLayer || [];
      const consent = newConsent
        ? {
            analytics: newConsent.analytics ?? this.checkConsentMode(),
            marketing: newConsent.marketing ?? false,
            preferences: newConsent.preferences ?? false,
          }
        : this.getConsentCookie();

      if (newConsent) {
        this.setConsentCookie(consent);
      }

      const consentObject = this.buildConsentModeObject(consent);
      gtag('consent', 'update', consentObject);
      window.dataLayer.push({ event: 'consent-update' });
      IubendaConsent.debug('dataLayerDebug', 'consent-update', consentObject);
    }

    /**
     * @description Bridges Iubenda consent callback with Google Consent Mode and Shopify Customer Privacy API.
     * Should be called from Iubenda's onPreferenceExpressed callback.
     * @param {Object} [preferences] - Iubenda preferences object. If empty, reads from _iub.cs.api.
     */
    static handleIubendaConsent(preferences) {
      const prefs =
        preferences && Object.keys(preferences).length > 0
          ? preferences
          : window._iub && window._iub.cs && window._iub.cs.api
            ? window._iub.cs.api.getPreferences()
            : null;

      if (!prefs || !prefs.purposes) return;

      // Map Iubenda purposes to our consent state. Analytics is forced to true under Consent Mode
      const { purposes } = prefs;
      const consent = {
        analytics: this.checkConsentMode() ? true : (purposes['4'] ?? false),
        marketing: purposes['5'] ?? false,
        preferences: purposes['3'] ?? false,
      };

      this.setConsentCookie(consent);
      this.updateConsent(consent);

      // Bridge with Shopify Customer Privacy API
      if (window.Shopify && window.Shopify.customerPrivacy) {
        window.Shopify.customerPrivacy.setTrackingConsent(
          {
            analytics: this.checkConsentMode() ? true : consent.analytics,
            marketing: consent.marketing,
            preferences: consent.preferences,
          },
          () => IubendaConsent.debug('dataLayerDebug', 'Shopify consent updated'),
        );
      }
    }

    /**
     * @description Tracks the X on the banner. Called from Iubenda's `onConsentFirstGiven` callback, which runs
     * right before `onPreferenceFirstExpressed`.
     * @param {string} eventName - How the consent was given, `bannerXClose` for the X (documented value).
     */
    static handleConsentFirstGiven(eventName) {
      if (eventName !== 'bannerXClose') return;
      closedWithX = true;
      window.pushGA4Event?.({ event_name: 'banner_cookie_close' });
    }

    /**
     * @description Tracks accept/reject from both the banner and the preferences panel. Called from Iubenda's
     * `onPreferenceFirstExpressed` callback, which fires on the first choice and on every update, not on page views.
     * Accept needs at least one optional purpose on (necessary is always on, so it doesn't count), reject needs
     * every optional purpose off.
     * @param {Object} [preference] - Iubenda preferences object. If empty, reads from _iub.cs.api.
     */
    static handlePreferenceFirstExpressed(preference) {
      if (closedWithX) {
        closedWithX = false;
        return;
      }

      const purposes = preference?.purposes ?? window._iub?.cs?.api?.getPreferences()?.purposes;
      if (!purposes) return;

      // Purpose 1 (necessary) is always true, so it doesn't count as a choice
      const optionalPurposes = Object.entries(purposes).filter(([id]) => id !== '1');
      const anyAccepted = optionalPurposes.some(([_, value]) => value);

      if (anyAccepted) {
        const mappedPurposes = {
          necessary: purposes['1'] ?? false,
          functionality: purposes['2'] ?? false,
          experience: purposes['3'] ?? false,
          analytics: purposes['4'] ?? false,
          marketing: purposes['5'] ?? false,
        };
        const label = Object.entries(mappedPurposes)
          .filter(([_, value]) => value)
          .map(([key, _]) => key)
          .join(', ');
        window.pushGA4Event?.({ event_name: 'banner_cookie_accept', label });
      } else {
        window.pushGA4Event?.({ event_name: 'banner_cookie_reject' });
      }
    }
  }

  window.IubendaConsent = IubendaConsent;
})();
